import React, { useContext, useEffect } from 'react'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { UpdateClientStatus } from 'api/Clients/Types/UpdateClientStatus'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { isEmpty } from 'lodash'
import { useAppDispatch, useAppSelector } from 'store'
import {
  fetchClientKYCProvidersAction,
  fetchClientRestrictionAction,
} from 'store/clients/clients.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'

import {
  ClientRestrictionId,
  ClientRestrictionName,
} from '../../../../../api/Clients/Types/ClientRestrictions'
import { UpdateClientOther } from '../../../../../api/Clients/Types/UpdateClientOther'
import { ClientDetailsOtherForm } from './ClientDetailsOtherForm'

interface Props {
  data: ClientDetailedDto
  forceRefresh(): void
  updateClientStatus(values: UpdateClientStatus): void
}

const ClientDetailsOtherPage: React.FC<Props> = ({ data, forceRefresh, updateClientStatus }) => {
  const clientsApiClient = useApiClient(ClientsApiClient)
  const setToast = useContext(ToastContext)

  const restrictions = useAppSelector((state) => state.clients.restrictions)
  const kycProviders = useAppSelector((state) => state.clients.kycProviders)
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!(restrictions || []).length) {
      dispatch(fetchClientRestrictionAction(clientsApiClient))
    }
    if (isEmpty(kycProviders)) {
      dispatch(fetchClientKYCProvidersAction(clientsApiClient))
    }
  }, [restrictions, kycProviders])

  const onSubmit = async (values: UpdateClientOther) => {
    await clientsApiClient.updateOtherOptions(data.id, values)
    setToast(successToast('Client updated successfully.'))
    forceRefresh()
  }

  const hasInterestRatesRestriction = data.restrictions.some(
    (x) =>
      x.name === ClientRestrictionName.InterestRatesDisabled &&
      x.id === ClientRestrictionId.InterestRatesDisabled
  )

  const hasExcludeBonusCampaignRestriction = data.restrictions.some(
    (x) =>
      x.name === ClientRestrictionName.ExcludeFromBonusCampaign &&
      x.id === ClientRestrictionId.ExcludeFromBonusCampaign
  )

  const hasLuckyDrawsRestriction = data.restrictions.some(
    (x) =>
      x.name === ClientRestrictionName.ExcludeFromLuckyDraw &&
      x.id === ClientRestrictionId.ExcludeFromLuckyDraw
  )

  const handleAllowMultiTierRegistration = async () => {
    await clientsApiClient.allowMultiTierRegistration(data.id, {
      multiTierRegistrationAllowed: !data.multiTierRegistrationAllowed,
    })
    forceRefresh()
  }

  const toggleInterestRatesRestriction = async () => {
    await clientsApiClient.toggleInterestRatesRestriction(data.id, {
      hasInterestRatesRestriction: !hasInterestRatesRestriction,
    })
    forceRefresh()
  }

  const toggleBonusCampaignExclusion = async () => {
    await clientsApiClient.toggleRestrictionFromBonusCampaign(data.id, {
      hasBonusCampaignRestriction: !hasExcludeBonusCampaignRestriction,
    })
    forceRefresh()
  }

  const toggleLuckyDrawExclusion = async () => {
    await clientsApiClient.toggleLuckyDrawExclusion(data.id, {
      exclude: !hasLuckyDrawsRestriction,
    })
    forceRefresh()
  }

  return (
    <ClientDetailsOtherForm
      clientDetails={data}
      onSubmit={onSubmit}
      updateClientStatus={updateClientStatus}
      onMultiTierRegistrationAllow={handleAllowMultiTierRegistration}
      onToggleInterestRatesRestriction={toggleInterestRatesRestriction}
      onToggleBonusCampaignExclusion={toggleBonusCampaignExclusion}
      onToggleLuckyDrawExclusion={toggleLuckyDrawExclusion}
    />
  )
}

export default ClientDetailsOtherPage
